import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { coverFor } from "@/lib/covers";
import { articles as sampleArticles } from "@/pages/Intelligence";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, ChevronRight } from "lucide-react";

const articleData = {
  "ai-agents-economic-actors": {
    category: "Artificial Intelligence",
    title: "AI Agents Move from Assistants to Economic Actors",
    subtitle: "A major shift is underway as AI agents begin performing economic roles with measurable value across industries.",
    date: "Oct 13, 2026",
    readTime: "5 min read",
    whatHappened: "Multiple major AI companies have announced expanded agent capabilities that allow AI systems to perform tasks that previously required human labor, including research, analysis, customer support and operational coordination. These developments indicate a move from simple automation to genuine economic contribution.",
    whyItMatters: "This signals a fundamental shift in how work is structured, valued and rewarded. As AI agents take on more meaningful economic activity, organizations will need to redesign roles, rethink talent development and focus more intentionally on uniquely human capabilities.",
    whoShouldCare: ["Executives and business leaders", "Students and early-career professionals", "Educators and workforce organizations", "Policymakers and economic development leaders"],
    raulvinsSignal: "The real signal isn't just that AI can perform another task. It's that the economic value of performing that task is beginning to collapse. The next advantage will come from people who combine human judgment, creativity and adaptive intelligence with AI capabilities.",
    whatToWatch: [
      "How organizations redesign entry-level roles",
      "Which skills increase in value",
      "How education systems respond",
      "Regulatory and ethical guardrails",
      "Emerging opportunities for new human-AI collaboration models",
    ],
    sources: [
      { title: "OpenAI Agent Capabilities Report", url: "#" },
      { title: "World Economic Forum Future of Jobs 2026", url: "#" },
      { title: "McKinsey & Company AI Workforce Impact Study", url: "#" },
    ],
  },
};

const defaultArticle = {
  category: "Intelligence",
  title: "Article Coming Soon",
  subtitle: "This article is currently being developed.",
  date: "2026",
  readTime: "5 min read",
  whatHappened: "This article is part of our upcoming coverage. Check back soon for the full analysis.",
  whyItMatters: "We cover developments that matter for the future of work, education, and human opportunity.",
  whoShouldCare: ["Leaders", "Educators", "Professionals", "Policymakers"],
  raulvinsSignal: "Every development in AI tells us something about the future of human value. The key is learning to read those signals before they become obvious.",
  whatToWatch: ["Emerging trends in AI adoption", "Workforce transformation patterns", "Education innovation"],
  sources: [],
};

const API = process.env.REACT_APP_BACKEND_URL;

const fromCms = (a) => ({
  category: a.category || "Intelligence",
  title: a.title,
  subtitle: a.subtitle || a.excerpt,
  date: a.published_date ? new Date(a.published_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "",
  readTime: `${Math.max(1, Math.round([a.what_happened, a.why_it_matters, a.raulvins_signal, a.body].join(" ").split(/\s+/).filter(Boolean).length / 220))} min read`,
  image: coverFor(a),
  author: a.author,
  whatHappened: a.what_happened,
  whyItMatters: a.why_it_matters,
  whoShouldCare: a.who_should_care || [],
  raulvinsSignal: a.raulvins_signal,
  whatToWatch: a.what_to_watch || [],
  body: a.body,
  sources: (a.sources || []).filter((s) => s.title),
});

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-display text-2xl font-semibold text-white italic mb-4">{title}</h2>
    {children}
  </section>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-[var(--text-secondary)]">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-violet)] mt-2 flex-shrink-0" />
        {item}
      </li>
    ))}
  </ul>
);

const ArticlePage = () => {
  const { slug } = useParams();
  const [cms, setCms] = useState(undefined);

  useEffect(() => {
    setCms(undefined);
    fetch(`${API}/api/articles/${slug}`).then((r) => (r.ok ? r.json() : null)).then((d) => setCms(d ? fromCms(d) : null)).catch(() => setCms(null));
  }, [slug]);

  if (cms === undefined) return <main className="pt-24 min-h-screen" data-testid="article-loading" />;
  const sample = sampleArticles.find((a) => a.slug === slug);
  const base = cms || articleData[slug] || (sample ? { ...defaultArticle, title: sample.title, subtitle: sample.summary, category: sample.category, date: sample.date, readTime: `${sample.readTime} read` } : defaultArticle);
  const article = { ...base, image: base.image || (cms || articleData[slug] || sample ? coverFor({ slug, category: base.category }) : "") };

  return (
    <main className="pt-24 pb-20 min-h-screen">
      <div className="max-w-4xl mx-auto px-6 md:px-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mb-8">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/intelligence" className="hover:text-white transition-colors">Intelligence</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[var(--accent-violet-light)]">{article.category}</span>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Category */}
          <span className="category-badge bg-violet-600/20 text-violet-300 mb-4 inline-block">
            {article.category}
          </span>

          {/* Title */}
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white italic leading-tight mb-4" data-testid="article-title-public">
            {article.title}
          </h1>
          <p className="text-lg text-[var(--text-secondary)] mb-6">{article.subtitle}</p>

          {/* Meta */}
          <div className="flex items-center gap-4 text-sm text-[var(--text-secondary)] mb-10 pb-8 border-b border-[rgba(124,58,237,0.1)]">
            <span>By <span className="text-white">{article.author || "Raulvin Coke"}</span></span>
            <span>{article.date}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{article.readTime}</span>
          </div>

          {article.image && <img src={article.image} alt={article.title} className="w-full aspect-video object-cover rounded-xl mb-10" data-testid="article-featured-image-public" />}

          {/* Content */}
          <div className="grid lg:grid-cols-[1fr_320px] gap-10">
            <div className="space-y-10">
              {article.whatHappened && <Section title="What Happened"><p className="text-[var(--text-secondary)] leading-relaxed">{article.whatHappened}</p></Section>}
              {article.whyItMatters && <Section title="Why It Matters"><p className="text-[var(--text-secondary)] leading-relaxed">{article.whyItMatters}</p></Section>}
              {article.body && (
                <Section title="Analysis">
                  <div className="space-y-4">{article.body.split(/\n\s*\n/).map((p, i) => <p key={i} className="text-[var(--text-secondary)] leading-relaxed">{p}</p>)}</div>
                </Section>
              )}
              {article.whoShouldCare.length > 0 && <Section title="Who Should Care"><Bullets items={article.whoShouldCare} /></Section>}
              {article.whatToWatch.length > 0 && <Section title="What to Watch Next"><Bullets items={article.whatToWatch} /></Section>}
              {article.sources.length > 0 && (
                <Section title="Sources">
                  <ol className="space-y-2 list-decimal list-inside">
                    {article.sources.map((s, i) => (
                      <li key={i} className="text-sm text-[var(--text-secondary)]">
                        {s.url && s.url !== "#" ? <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--accent-violet-light)] underline-offset-4 hover:underline">{s.title}</a> : s.title}
                      </li>
                    ))}
                  </ol>
                </Section>
              )}
            </div>

            {/* Sidebar: Raulvin's Signal */}
            {article.raulvinsSignal && (
            <aside className="lg:sticky lg:top-24 self-start">
              <div className="bg-[var(--bg-card)] border border-[var(--accent-violet)]/20 rounded-xl p-6" data-testid="raulvins-signal-public">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-[var(--accent-violet-light)] font-semibold text-sm">Raulvin's Signal</span>
                </div>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed italic">
                  {article.raulvinsSignal}
                </p>
              </div>
            </aside>
            )}
          </div>

          {/* Back */}
          <div className="mt-12 pt-8 border-t border-[rgba(124,58,237,0.1)]">
            <Link to="/intelligence" className="inline-flex items-center gap-2 text-[var(--accent-violet-light)] hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Intelligence
            </Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default ArticlePage;
